import { slotHeight, slotMinutes } from './constants';
import type { Session } from './types';
import {
  formatISODate,
  formatTimeFromMinutes,
  formatTimeRange,
  getEventStyle,
  isSameDay,
  parseTimeToMinutes,
} from './utils';

type SessionsCalendarProps = {
  data: {
    isLoading: boolean;
    monthLabel: string;
    weekDates: Date[];
    weekSessions: Session[];
  };
  actions: {
    onPrevWeek: () => void;
    onNextWeek: () => void;
    onToday: () => void;
  };
};


const formatDateRange = (dates: Date[]): string => {
  if (dates.length === 0) return '';
  
  const startDate = dates[0];
  const endDate = dates[dates.length - 1];
  const month = startDate.toLocaleDateString('en-US', { month: 'long' });
  const year = startDate.getFullYear();
  
  if (
    startDate.getMonth() === endDate.getMonth() &&
    startDate.getFullYear() === endDate.getFullYear()
  ) {
    return `${startDate.getDate()}–${endDate.getDate()} ${month}, ${year}`;
  }
  
  const endMonth = endDate.toLocaleDateString('en-US', { month: 'long' });
  return `${startDate.getDate()} ${month} – ${endDate.getDate()} ${endMonth}, ${year}`;
};

const SessionsCalendar = ({ data, actions }: SessionsCalendarProps) => {
  if (data.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <div className="h-5 w-40 rounded-full bg-slate-100" />
        <div className="h-[360px] w-full rounded-xl border border-slate-100 bg-slate-50" />
      </div>
    );
  }

  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const defaultStart = 8 * 60;
  const defaultEnd = 18 * 60;

  let minStart = Infinity;
  let maxEnd = -Infinity;
  data.weekSessions.forEach((session) => {
    minStart = Math.min(minStart, parseTimeToMinutes(session.start));
    maxEnd = Math.max(maxEnd, parseTimeToMinutes(session.end));
  });

  const timeRange = {
    start:
      data.weekSessions.length === 0
        ? defaultStart
        : Math.min(defaultStart, minStart),
    end:
      data.weekSessions.length === 0
        ? defaultEnd
        : Math.max(defaultEnd, maxEnd),
  };

  const totalSlots = Math.max(
    1,
    Math.ceil((timeRange.end - timeRange.start) / slotMinutes)
  );
  const gridHeight = totalSlots * slotHeight;

  const showNowLine =
    data.weekDates.some((date) => isSameDay(date, now)) &&
    nowMinutes >= timeRange.start &&
    nowMinutes <= timeRange.end;
  const nowTop = ((nowMinutes - timeRange.start) / slotMinutes) * slotHeight;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2 px-0 py-2">
        <button
          type="button"
          className="flex items-center gap-1.5 rounded-md border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-900 hover:bg-slate-50 transition"
          aria-label="Go to today"
          onClick={actions.onToday}
        >
          <svg
            className="h-3 w-3"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <path d="M16 2v4M8 2v4M3 10h18" />
          </svg>
          Today
        </button>
        
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="flex h-8 w-8 items-center justify-center rounded-md border border-slate-300 text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition"
            aria-label="Previous week"
            onClick={actions.onPrevWeek}
          >
            <svg
              className="h-3 w-3"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          
          <span className="text-xs font-semibold text-slate-900 min-w-[140px] text-center">
            {formatDateRange(data.weekDates)}
          </span>
          
          <button
            type="button"
            className="flex h-8 w-8 items-center justify-center rounded-md border border-slate-300 text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition"
            aria-label="Next week"
            onClick={actions.onNextWeek}
          >
            <svg
              className="h-3 w-3"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M9 6l6 6-6 6" />
            </svg>
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[800px]">
          <div className="grid grid-cols-[60px_repeat(7,minmax(100px,_1fr))] border-b border-slate-200 bg-slate-50 pb-2 pt-1 text-xs text-slate-400">
            <div />
            {data.weekDates.map((date) => {
              const dayLabel = date.toLocaleDateString('en-US', {
                weekday: 'short',
              }).toUpperCase();
              const isToday = isSameDay(date, now);
              return (
                <div key={date.toISOString()} className="px-1 text-center">
                  <p
                    className={`text-[10px] font-medium uppercase tracking-[0.1em] mb-1 ${
                      isToday ? 'text-blue-600' : 'text-slate-500'
                    }`}
                  >
                    {dayLabel}
                  </p>
                  <p
                    className={`text-sm font-semibold ${
                      isToday ? 'text-blue-600' : 'text-slate-900'
                    }`}
                  >
                    {date.getDate()}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="relative">
            <div className="grid grid-cols-[60px_repeat(7,minmax(100px,_1fr))]">
              <div className="flex flex-col" style={{ height: gridHeight }}>
                {Array.from({ length: totalSlots }).map((_, index) => {
                  const minutes = timeRange.start + index * slotMinutes;
                  const label = minutes % 60 === 0 ? formatTimeFromMinutes(minutes) : '';
                  return (
                    <div
                      key={minutes}
                      className="flex h-6 items-start justify-end pr-2 text-[9px] text-slate-400"
                    >
                      {label}
                    </div>
                  );
                })}
              </div>
              {data.weekDates.map((date) => {
                const isoDate = formatISODate(date);
                const isToday = isSameDay(date, now);
                const daySessions = data.weekSessions.filter(
                  (session) => session.date === isoDate
                );
                return (
                  <div
                    key={isoDate}
                    className={`relative border-l border-slate-100 ${
                      isToday ? 'bg-blue-50/60' : ''
                    }`}
                    style={{ height: gridHeight }}
                  >
                    <div className="absolute inset-0">
                      {Array.from({ length: totalSlots }).map((_, row) => (
                        <div
                          key={`row-${isoDate}-${row}`}
                          className="h-6 border-t border-slate-100"
                        />
                      ))}
                    </div>
                    {daySessions.map((session) => {
                      const startMinutes = parseTimeToMinutes(session.start);
                      const endMinutes = parseTimeToMinutes(session.end);
                      const top =
                        ((startMinutes - timeRange.start) / slotMinutes) *
                        slotHeight;
                      const rawHeight =
                        ((endMinutes - startMinutes) / slotMinutes) * slotHeight;
                      const height = Math.max(20, rawHeight - 6);
                      const style = getEventStyle(session.student);

                      return (
                        <div
                          key={session.id}
                          className={`absolute left-1 right-1 rounded-md border px-1 py-0.5 text-[9px] shadow-sm ${style}`}
                          style={{ top: Math.round(top + 2), height: Math.round(height) }}
                        >
                          <p className="font-semibold">{session.student}</p>
                          <p>{formatTimeRange(session.start, session.end)}</p>
                        </div>
                      );
                    })}
                    {isToday ? (
                      <div className="pointer-events-none absolute inset-0 z-10 border-2 border-blue-500" />
                    ) : null}
                  </div>
                );
              })}
            </div>

            {showNowLine ? (
              <div
                className="pointer-events-none absolute left-[60px] right-0 border-t border-rose-400"
                style={{ top: nowTop }}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionsCalendar;
